/**
 * Product analytics (§16). Pseudonymous funnel events sent to the API (when
 * VITE_API_URL is set) via sendBeacon so they survive navigation and never block
 * the UI. No-ops without a backend. Analytics must NEVER break the app.
 */
import { getAnonId } from './history';

const API = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '');

/** The event taxonomy, the install→scan→paid funnel spine (§16.3). */
export const EVENTS = {
  scanStarted: 'scan_started',
  quizCompleted: 'quiz_completed',
  consentGranted: 'consent_granted',
  analyzeStarted: 'analyze_started',
  resultsViewed: 'results_viewed',
  cardShared: 'card_shared',
  rescan: 'rescan',
  pricingViewed: 'pricing_viewed',
  checkoutStarted: 'checkout_started',
  guideViewed: 'guide_viewed',
  downloadViewed: 'download_viewed',
  referralVisit: 'referral_visit',
  referralCopied: 'referral_copied',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

export function track(name: EventName | string, props: Record<string, unknown> = {}): void {
  try {
    if (import.meta.env.DEV) console.debug('[facet:event]', name, props);
    if (!API) return;
    const body = JSON.stringify({ name, props, anonId: getAnonId() });
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(`${API}/v1/events`, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(`${API}/v1/events`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      });
    }
  } catch {
    /* swallow, analytics is best-effort */
  }
}
